import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import moment from 'moment';
import { tap } from 'rxjs';

import { InsurerFilterResponse } from 'src/app/models/declartion-report-responses/declaration-report-response';
import { ExportLPGDeclarationReportResponse, LPGDriversClubDeclarationLine } from 'src/app/models/declartion-report-responses/lpg-drivers-club-declaration-report-response';
import { ExportFileType } from 'src/app/models/enums/report-type.enum';
import { TransactionType } from 'src/app/models/enums/transaction-type.enum';
import { ReportHistoryService } from 'src/app/services/report-history.service';
import { ReportService } from 'src/app/services/report.service';
import { AppConstants, FORMAT_ONLY_DATE, NO_RESULTS_FOUND, PagingConstant } from 'src/app/shared/constants/constants';
import { HttpBaseService } from 'src/app/shared/services/http-base.service';
import { Utility } from 'src/app/utils/utility';

@Component({
    selector: 'app-lpg-declarations-report',
    templateUrl: './lpg-declarations-report.component.html',
    styleUrls: ['./lpg-declarations-report.component.scss'],
    standalone: false
})
export class LpgDeclarationsReportComponent implements OnInit {

  @Input()
  covers: string[];

  @Input()
  insurers: InsurerFilterResponse[];

  @Input()
  provider: string;

  @Input()
  products: string[];

  @Input()
  productNames: string;

  @Input()
  dateFrom: Date;

  @Input()
  dateTo: Date;

  @Input()
  sourceDialog: ExportLPGDeclarationReportResponse;

  @Input()
  historyItemId: string;

  transactionType = TransactionType;
  allOptionTxt: string = 'All';
  notfound = '';
  pagingConstant = PagingConstant;
  pageSizeOptions: number[] = PagingConstant.pageSizeOptions;
  totalRows: number = 0;
  searchResults: LPGDriversClubDeclarationLine[] = [];
  result: any;
  dataSource = new MatTableDataSource<LPGDriversClubDeclarationLine>([]);
  public appConstants = AppConstants;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns = [
    'policyNumber',
    'inceptionDate',
    'renewalDate',
    'transactionType',
    'policyHolder',
    'address',
    'postcode',
    'country',
    'vehicleMake',
    'vehicleModel',
    'vehicleYear',
    'vehicleRegistration',
    'grossPremium',
    'netPremium',
    'commission',
    'fee',
    'ipt',
    'premiumToLPG'
  ];

  constructor(
    private readonly _reportService: ReportService,
    private readonly httpService: HttpBaseService,
    private readonly reportHistoryService: ReportHistoryService) {
  }

  get notFoundMessage() {
    return this.notfound;
  }

  ngOnInit(): void {
    if (this.sourceDialog != null) {
      this.result = this.sourceDialog;
      this.productNames = this.sourceDialog.products;
      let source = this.sortData(this.sourceDialog.declarationLines, '', 'asc');
      let cloneArray = source.slice();
      let data = source.splice(0, 10);
      this.sourceDialog.declarationLines = cloneArray;
      this.dataSource = new MatTableDataSource<LPGDriversClubDeclarationLine>(data);
      this.totalRows = this.sourceDialog.declarationLines.length;
    }
  }

  onSubmit() {
    this.resetTable();
    this.loadData(true);
  }

  resetTable() {
    this.dataSource = new MatTableDataSource<LPGDriversClubDeclarationLine>([]);
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = PagingConstant.pageSizeOptions[1];
    this.totalRows = 0;
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    this.sort.sortChange
      .pipe(
        tap(() => this.loadData(false))
      )
      .subscribe();

    this.paginator.page
      .pipe(
        tap(() => this.loadData(false))
      )
      .subscribe();
  }

  checkNotFoundResults(totalRecords) {
    this.notfound = totalRecords === 0 ? NO_RESULTS_FOUND : '';
  }

  getTableStyle() {
    let display = this.totalRows == 0 ? 'none' : 'block';
    return {
      'display': display
    };
  }

  loadData(isLoadTotal: boolean = false) {
    const pageIndex = this.paginator.pageIndex;
    const pageSize = this.paginator.pageSize;
    const sortField = this.sort.active;
    const sortDirection = this.sort.direction;
    if (this.sourceDialog) {
      let source = this.sortData(this.sourceDialog.declarationLines, sortField, sortDirection);
      let cloneArray = source.slice();
      let data = source.splice(pageIndex * pageSize, pageSize);
      this.sourceDialog.declarationLines = cloneArray;
      this.dataSource = new MatTableDataSource<LPGDriversClubDeclarationLine>(data);
    }
    else {
      let covers = this.covers.filter((x: string) => x != this.allOptionTxt);
      let arrInsurerIds: string[] = [];
      let ids = this.insurers.map((x: { ids: string[] }) => x.ids);
      ids.forEach((x: string[]) => arrInsurerIds = arrInsurerIds.concat(x));
      if (covers.length == 0) {
        this.totalRows = 0;
        this.checkNotFoundResults(this.totalRows);
      }
      else {
        this._reportService.getLPGDriversClubDeclarationReport(covers, arrInsurerIds, moment(this.dateFrom).format(FORMAT_ONLY_DATE), moment(this.dateTo).format(FORMAT_ONLY_DATE), isLoadTotal, sortField, sortDirection, pageIndex, pageSize)
          .subscribe((report) => {
            if (isLoadTotal) {
              this.result = report;
            }
            else {
              this.result.declarationLines = report.declarationLines;
            }
            this.searchResults = report.declarationLines;
            this.dataSource = new MatTableDataSource<LPGDriversClubDeclarationLine>(this.searchResults);
            this.totalRows = report.total;
            this.checkNotFoundResults(this.totalRows);
          })
      }
    }
  }

  export(type: ExportFileType) {
    let url: string = '';
    const fromMonth = Utility.getMonthName(this.dateFrom);
    const toMonth = Utility.getMonthName(this.dateTo);
    const fileName = fromMonth != toMonth
      ? `LPGDeclarationReport ${fromMonth}_${toMonth}_${new Date().toLocaleDateString("en-UK")}`
      : `LPGDeclarationReport ${toMonth}_${new Date().toLocaleDateString("en-UK")}`;

    if (!this.sourceDialog) {
      let covers = this.covers.filter((x: string) => x != this.allOptionTxt);
      let arrInsurerIds: string[] = [];
      let ids = this.insurers.map((x: { ids: string[] }) => x.ids);
      ids.forEach((x: string[]) => arrInsurerIds = arrInsurerIds.concat(x));
      let providers: string[] = [];
      providers.push(this.provider)
      let arrProductIds = this.products.filter((x: string) => x != this.allOptionTxt);

      if (covers.length > 0) {
        url = this._reportService.exportLPGDriversClubDeclarationReport(covers, arrInsurerIds, moment(this.dateFrom).format(FORMAT_ONLY_DATE), moment(this.dateTo).format(FORMAT_ONLY_DATE),
          providers, arrProductIds, type);
      }
    }
    else {
      url = this.reportHistoryService.exportLPGDeclarationReport(this.historyItemId, type)
    }

    if (url) {
      this.httpService.downloadFile(url).subscribe(
        (data: BlobPart) => {
          const blob = new Blob([data], { type: this.getContentType(type) });
          let fileURL = window.URL.createObjectURL(blob);
          let link = document.createElement('a');
          link.style.display = "none";
          link.href = fileURL;
          link.download = fileName;
          link.target = '_blank';
          link.click();

          setTimeout(() => {
            link.remove();
            window.URL.revokeObjectURL(fileURL);
          }, 100);
        }
      );
    }
  }

  sortData(data: LPGDriversClubDeclarationLine[], sortField: string, sortDirection: string): LPGDriversClubDeclarationLine[] {
    if (sortDirection == 'asc') {
      switch (sortField) {
        case 'inceptionDate':
          data.sort((a: LPGDriversClubDeclarationLine, b: LPGDriversClubDeclarationLine) => {
            return (new Date(a.inceptionDate)).getTime() - (new Date(b.inceptionDate)).getTime()
          });
          break;
        case 'renewalDate':
          data.sort((a: LPGDriversClubDeclarationLine, b: LPGDriversClubDeclarationLine) => {
            return (new Date(a.renewalDate)).getTime() - (new Date(b.renewalDate)).getTime()
          });
          break;
        default:
          data.sort((a: LPGDriversClubDeclarationLine, b: LPGDriversClubDeclarationLine) => {
            return (new Date(a.inceptionDate)).getTime() - (new Date(b.inceptionDate)).getTime()
          });
      }
    }
    else {
      switch (sortField) {
        case 'inceptionDate':
          data.sort((a: LPGDriversClubDeclarationLine, b: LPGDriversClubDeclarationLine) => {
            return (new Date(b.inceptionDate)).getTime() - (new Date(a.inceptionDate)).getTime()
          });
          break;
        case 'renewalDate':
          data.sort((a: LPGDriversClubDeclarationLine, b: LPGDriversClubDeclarationLine) => {
            return (new Date(b.renewalDate)).getTime() - (new Date(a.renewalDate)).getTime()
          });
          break;
        default:
          data.sort((a: LPGDriversClubDeclarationLine, b: LPGDriversClubDeclarationLine) => {
            return (new Date(b.inceptionDate)).getTime() - (new Date(a.inceptionDate)).getTime()
          });
      }
    }

    return data;
  }

  getContentType(fileType: ExportFileType) {
    switch (fileType) {
      case ExportFileType.Pdf:
        return "application/pdf";

      case ExportFileType.Csv:
        return "text/csv";

      case ExportFileType.Excel:
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      default:
        return '';
    }
  }

  exportExcel() {
    this.export(ExportFileType.Excel);
  }

  exportCsv() {
    this.export(ExportFileType.Csv);
  }

  exportPdf() {
    this.export(ExportFileType.Pdf);
  }
}
